import { Show } from "@refinedev/antd";
import { useCustom, useCustomMutation, useNavigation } from "@refinedev/core";
import { useParams } from "react-router-dom";
import {
  Button,
  Card,
  Descriptions,
  Input,
  message,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { useState } from "react";
import { TOKEN_KEY } from "@/authProvider";
import { apiUrl } from "@/consts";
import type {
  LottoLicence,
  LottoLicenceHistory,
  LottoLicenceHistoryAction,
  LottoLicenceStatus,
} from "./types";

const { Text } = Typography;

const STATUS_COLOR: Record<LottoLicenceStatus, string> = {
  DRAFT: "default",
  SUBMITTED: "gold",
  APPROVED: "success",
  REJECTED: "red",
};

const ACTION_LABEL: Record<LottoLicenceHistoryAction, string> = {
  SNAPSHOT: "Snapshot",
  CREATED: "Created",
  UPDATED: "Updated",
  SUBMITTED: "Submitted",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  REVOKED: "Revoked",
};

function formatDate(value: string | null) {
  return value ? new Date(value).toLocaleString() : "—";
}

function apiError(err: unknown): string {
  const e = err as { message?: string; response?: { data?: { error?: string; message?: string } } };
  return e?.response?.data?.error || e?.response?.data?.message || e?.message || "Request failed";
}

export function LottoLicenceShow() {
  const { id } = useParams<{ id: string }>();
  const { list } = useNavigation();
  const { mutate, isLoading: mutating } = useCustomMutation();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [openingCertificate, setOpeningCertificate] = useState(false);
  const [openingHistoryId, setOpeningHistoryId] = useState<string | null>(null);

  const licenceQuery = useCustom<{ data: LottoLicence }>({
    url: `${apiUrl}/lotto-license/${id}`,
    method: "get",
    queryOptions: { enabled: Boolean(id) },
  });

  const historyQuery = useCustom<{ data: LottoLicenceHistory[] }>({
    url: `${apiUrl}/lotto-license/${id}/history`,
    method: "get",
    queryOptions: { enabled: Boolean(id) },
  });

  const licence = licenceQuery.data?.data?.data;
  const refetch = licenceQuery.refetch;
  const refetchHistory = historyQuery.refetch;

  function approve() {
    if (!id) return;
    mutate(
      {
        url: `${apiUrl}/lotto-license/${id}/approve`,
        method: "patch",
        values: {},
      },
      {
        onSuccess: () => {
          message.success("Licence approved");
          refetch?.();
          refetchHistory?.();
        },
        onError: (err) => message.error(apiError(err)),
      },
    );
  }

  function reject() {
    if (!id) return;
    const reason = rejectionReason.trim();
    if (!reason) {
      message.error("Rejection reason is required");
      return;
    }
    mutate(
      {
        url: `${apiUrl}/lotto-license/${id}/reject`,
        method: "patch",
        values: { rejectionReason: reason },
      },
      {
        onSuccess: () => {
          message.success(licence?.status === "APPROVED" ? "Licence revoked" : "Licence rejected");
          setRejectOpen(false);
          setRejectionReason("");
          refetch?.();
          refetchHistory?.();
        },
        onError: (err) => message.error(apiError(err)),
      },
    );
  }

  async function openCertificate() {
    if (!id) return;
    setOpeningCertificate(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const response = await fetch(`${apiUrl}/lotto-license/${id}/certificate`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = (await response.json().catch(() => ({}))) as {
          error?: string;
          message?: string;
        };
        throw new Error(err.error || err.message || "Could not open certificate");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      message.error(apiError(err));
    } finally {
      setOpeningCertificate(false);
    }
  }

  async function openHistoryCertificate(historyId: string) {
    if (!id) return;
    setOpeningHistoryId(historyId);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const response = await fetch(`${apiUrl}/lotto-license/${id}/history/${historyId}/certificate`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = (await response.json().catch(() => ({}))) as {
          error?: string;
          message?: string;
        };
        throw new Error(err.error || err.message || "Could not open certificate");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      message.error(apiError(err));
    } finally {
      setOpeningHistoryId(null);
    }
  }

  return (
    <Show
      title="Lotto licence"
      resource="lotto-licences"
      isLoading={licenceQuery.isLoading}
      headerButtons={() => (
        <Button onClick={() => list("lotto-licences")}>Back to licences</Button>
      )}
    >
      {licence ? (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Space wrap>
            {licence.status !== "APPROVED" && (
              <Popconfirm
                title="Approve this lotto licence?"
                onConfirm={approve}
                okButtonProps={{ loading: mutating }}
              >
                <Button type="primary" loading={mutating}>
                  Approve
                </Button>
              </Popconfirm>
            )}
            {licence.status !== "REJECTED" && (
              <Button
                danger
                loading={mutating}
                onClick={() => {
                  setRejectionReason("");
                  setRejectOpen(true);
                }}
              >
                {licence.status === "APPROVED" ? "Revoke" : "Reject"}
              </Button>
            )}
            {licence.certificateFileID && (
              <Button onClick={openCertificate} loading={openingCertificate}>
                {openingCertificate ? "Opening certificate..." : "View certificate PDF"}
              </Button>
            )}
          </Space>

          <Card size="small" title="Licence details">
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLOR[licence.status]}>{licence.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Club">
                {licence.club?.name ?? "Unknown club"}
                {licence.club?.slug ? <Text type="secondary"> ({licence.club.slug})</Text> : null}
              </Descriptions.Item>
              <Descriptions.Item label="Licence number">
                {licence.licenseNumber || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Issuing authority">
                {licence.issuingAuthority || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Jurisdiction">
                {licence.jurisdiction || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Regulator">
                {licence.regulatorName || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Licence class">
                {licence.licenceClass || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Expires">
                {licence.expiresAt ? new Date(licence.expiresAt).toLocaleDateString() : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Certificate">
                {licence.certificateFileID ? (
                  <Button type="link" onClick={openCertificate} loading={openingCertificate}>
                    {openingCertificate ? "Opening..." : "View PDF"}
                  </Button>
                ) : (
                  "Not provided"
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card size="small" title="Review">
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Submitted">
                {formatDate(licence.submittedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Reviewed">
                {formatDate(licence.reviewedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Reviewed by">
                {licence.reviewedBy?.email ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Rejection reason">
                {licence.rejectionReason || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Notes">
                {licence.notes || "—"}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card size="small" title="Licence history">
            <Table<LottoLicenceHistory>
              rowKey="id"
              size="small"
              loading={historyQuery.isLoading}
              dataSource={historyQuery.data?.data?.data ?? []}
              pagination={{ pageSize: 8, hideOnSinglePage: true }}
              columns={[
                {
                  title: "When",
                  dataIndex: "createdAt",
                  render: (value: string) => formatDate(value),
                },
                {
                  title: "Action",
                  dataIndex: "action",
                  render: (value: LottoLicenceHistoryAction) => ACTION_LABEL[value] ?? value,
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  render: (value: LottoLicenceStatus) => (
                    <Tag color={STATUS_COLOR[value]}>{value}</Tag>
                  ),
                },
                {
                  title: "Licence",
                  dataIndex: "licenseNumber",
                  render: (value: string | null) => value || "—",
                },
                {
                  title: "Actor",
                  render: (_, row) => row.actor?.email ?? row.reviewedBy?.email ?? "—",
                },
                {
                  title: "Certificate",
                  dataIndex: "certificateFileID",
                  render: (value: string | null, row) =>
                    value ? (
                      <Button
                        size="small"
                        onClick={() => openHistoryCertificate(row.id)}
                        loading={openingHistoryId === row.id}
                      >
                        View PDF
                      </Button>
                    ) : (
                      "—"
                    ),
                },
              ]}
            />
          </Card>

          <Modal
            title={licence.status === "APPROVED" ? "Revoke this licence?" : "Reject this licence?"}
            open={rejectOpen}
            okText={licence.status === "APPROVED" ? "Revoke licence" : "Reject licence"}
            okButtonProps={{ danger: true, loading: mutating }}
            onOk={reject}
            onCancel={() => setRejectOpen(false)}
          >
            <Input.TextArea
              rows={4}
              value={rejectionReason}
              onChange={(event) => setRejectionReason(event.target.value)}
              placeholder="Reason shown to the club"
            />
          </Modal>
        </Space>
      ) : (
        <Text type="secondary">Licence not found.</Text>
      )}
    </Show>
  );
}
